import { FC, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  Add as CreateIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Box, Grid, IconButton, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';

import { InfoField, type InfoFieldChangeHandler } from '../../components';
import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';
import { AppTokenSecretModal, CreateAppTokenModal, DeleteAppTokenModal } from './components';

import {
  type CreateApplicationTokenDTO,
  useCreateApplicationTokenMutation,
  useDeleteApplicationTokenMutation,
  useGetApplicationQuery,
  useGetApplicationTokensQuery,
} from '../../api';
import { localToUTC, UTCToDateTime } from '../../utils/dates';
import { RouterPaths } from '../../router/paths';
import { useAppActions } from '../../utils/hooks';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import type { ApplicationToken } from '../../types';
import dayjs from 'dayjs';
import { permissionsCheck } from '../../utils/permission';

export const ApplicationTokens: FC = () => {
  const { t } = useTranslation();
  const { applicationUid } = useParams<'applicationUid'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditApplicationTokens = permissionsCheck(['WRITE_APPLICATION_TOKENS']);

  // api
  const { data: application, isLoading: isApplicationLoading } = useGetApplicationQuery(
    applicationUid ? { application_uid: applicationUid } : skipToken
  );
  const { data: tokens, isLoading: isTokensLoading } = useGetApplicationTokensQuery(
    applicationUid ? { application_uid: applicationUid } : skipToken
  );
  const [createAppToken, { isLoading: isCreateTokenLoading }] = useCreateApplicationTokenMutation();
  const [deleteAppToken, { isLoading: isDeleteTokenLoading }] = useDeleteApplicationTokenMutation();

  // variables
  const isLoading =
    isApplicationLoading || isTokensLoading || isCreateTokenLoading || isDeleteTokenLoading;
  const [activeToken, setActiveToken] = useState<ApplicationToken | null>(null);

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditApplicationTokens &&
      setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  // fields for forms logic
  const [editFields, setEditFields] = useState<CreateApplicationTokenDTO['body'] | null>(null);
  const handleEditFields: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // create token modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  // new token modal logic
  const [newTokenSecret, setNewTokenSecret] = useState('');
  const [newTokenModalShown, setNewTokenModalShown] = useState(false);
  const openNewTokenModal = (secret: string) => {
    setNewTokenModalShown(true);
    setNewTokenSecret(secret);
  };
  const closeNewTokenModal = () => {
    setNewTokenModalShown(false);
    setNewTokenSecret('');
  };

  // delete token modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (token: ApplicationToken) => {
    setDeleteModalShown(true);
    setActiveToken(token);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveToken(null);
  };

  // create token handler
  const createToken = async () => {
    if (!applicationUid || !editFields) return;

    const args = {
      application_uid: applicationUid,
      body: {
        name: editFields.name,
        expired_at: localToUTC(dayjs(editFields.expired_at).endOf('day')),
        allowed_ips: editFields.allowed_ips,
      },
    };

    try {
      const res = await createAppToken(args).unwrap();
      closeCreateModal();
      openNewTokenModal(res.token.secret_key);
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete token handler
  const deleteToken = async () => {
    if (!applicationUid || !activeToken) return;

    const args = {
      application_uid: applicationUid,
      application_token_uid: activeToken.uid,
    };

    try {
      await deleteAppToken(args).unwrap();
      closeDeleteModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('app-tokens.head-cells.uid') },
    { id: '2', label: t('app-tokens.head-cells.name') },
    { id: '3', label: t('app-tokens.head-cells.allowed-ips') },
    { id: '4', label: t('app-tokens.head-cells.created-at') },
    { id: '5', label: t('app-tokens.head-cells.expired-at') },
    {
      id: '6',
      label: t('app-tokens.head-cells.actions'),
      hidden: !hasPermissionToEditApplicationTokens,
    },
  ];

  const rows: Row[] | undefined = tokens?.tokens.map((it) => {
    const { uid, name, allowed_ips, created_at, expired_at } = it;

    const cells: RowCell[] = [
      { name: 'uid', value: uid },
      { name: 'name', value: name },
      {
        name: 'allowed_ips',
        component: (
          <Stack
            direction="row"
            justifyContent="flex-start"
            rowGap={1}
            columnGap={2}
            flexWrap="wrap"
          >
            {allowed_ips.map((ip, index) => (
              <Box key={index}>{ip}</Box>
            ))}
          </Stack>
        ),
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'expired_at',
        value: expired_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditApplicationTokens,
        component: (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(it)}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Stack direction="row" alignItems="flex-end" spacing={2} pl={3} pt={1}>
        <MuiLink underline="hover" component={Link} to={RouterPaths.ABFPaymentApps}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t('app-tokens.back-to-apps')}
            </Typography>
          </Stack>
        </MuiLink>
      </Stack>

      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('app-tokens.app-info.uid')}
              value={application?.application.uid}
              //
            />

            <InfoField
              name={t('app-tokens.app-info.name')}
              value={application?.application.name}
              //
            />

            <InfoField
              name={t('app-tokens.app-info.created-at')}
              value={application?.application.created_at}
              formattedValue={
                application?.application.created_at &&
                UTCToDateTime(application.application.created_at)
              }
              withoutDivider
            />
          </Stack>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateAppTokenModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createToken}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isCreateTokenLoading}
      />

      <AppTokenSecretModal
        open={newTokenModalShown}
        onClose={closeNewTokenModal}
        newTokenSecret={newTokenSecret}
      />

      <DeleteAppTokenModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteToken}
        isLoading={isDeleteTokenLoading}
      />
    </>
  );
};
